import { ReactNode, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Background from "@/components/ui/Background";
import { H1 } from "@/components/ui/Typography";
import DataToggle from "@/components/common/DataToggle";
import RangeMonthYearSelector from "@/components/common/RangeMonthYearSelector";
import CurrencyToggle from "@/components/common/CurrencyToggle";
import { useCurrency } from "@/context/CurrencyContext";
import { notFoundUrl } from "@/routes/urls";

type View = "overview" | "graph" | "monthly" | "raw";
type YM = { year: number; month: number };
type RangeFilter = { from: YM; to: YM };

interface FetchHookRange<T> {
  (
    clientId: string,
    connectionId: string,
    dateRange: RangeFilter,
    enabled: boolean
  ): { data: T };
}

export interface FinancialPagePnLRangeConfig<Overview, Graph, Monthly, Raw> {
  title: string;
  overviewHook: FetchHookRange<Overview>;
  graphHook?: FetchHookRange<Graph>;
  monthlyHook?: FetchHookRange<Monthly>;
  rawHook?: FetchHookRange<Raw>;
  OverviewComponent: React.FC<{
    data: Overview;
    startDate: string;
    endDate: string;
  }>;
  GraphComponent?: React.FC<{ data: Graph }>;
  MonthlyComponent?: React.FC<{
    data: Monthly;
    clientId: string;
    connectionId: string;
    range: RangeFilter;
  }>;
  RawComponent?: React.FC<{ data: Raw }>;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

export default function FinancialPagePnLRange<Overview, Graph, Monthly, Raw>({
  title,
  overviewHook,
  graphHook,
  monthlyHook,
  rawHook,
  OverviewComponent,
  GraphComponent,
  MonthlyComponent,
  RawComponent,
}: FinancialPagePnLRangeConfig<Overview, Graph, Monthly, Raw>): ReactNode {
  const { clientId, connectionId } = useParams<{
    clientId: string;
    connectionId: string;
  }>();
  const [view, setView] = useState<View>("overview");
  const { unit, setUnit } = useCurrency();

  const now = new Date();
  const [range, setRange] = useState<RangeFilter>({
    from: { year: now.getFullYear(), month: 1 },
    to: { year: now.getFullYear(), month: now.getMonth() + 1 },
  });

  if (!clientId || !connectionId) return <Navigate to={notFoundUrl} />;

  const { data: overviewData } = overviewHook(
    clientId,
    connectionId,
    range,
    view === "overview"
  );

  const { data: graphData } = graphHook
    ? graphHook(clientId, connectionId, range, view === "graph")
    : { data: undefined as unknown as Graph };

  const { data: monthlyData } = monthlyHook // ← NEW
    ? monthlyHook(clientId, connectionId, range, view === "monthly")
    : { data: undefined as unknown as Monthly };

  const { data: rawData } = rawHook
    ? rawHook(clientId, connectionId, range, view === "raw")
    : { data: undefined as unknown as Raw };

  const currentLabel = `${range.from.year}-${pad2(range.from.month)} - ${
    range.to.year
  }-${pad2(range.to.month)}`;
  const prev = {
    from: { year: range.from.year - 1, month: range.from.month },
    to: { year: range.to.year - 1, month: range.to.month },
  };
  const previousLabel = `${prev.from.year}-${pad2(prev.from.month)} - ${
    prev.to.year
  }-${pad2(prev.to.month)}`;

  return (
    <Background className="mx-auto px-4 py-8 flex flex-col min-h-screen">
      <div className="flex items-center justify-between mb-8 pl-2">
        <H1>{title}</H1>
      </div>

      <div className="relative flex items-center justify-between mb-2">
        <DataToggle active={view} onChange={setView} showMonthly={true} />{" "}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <CurrencyToggle active={unit} onChange={setUnit} />
        </div>
        <RangeMonthYearSelector value={range} onChange={setRange} />
      </div>

      <>
        {view === "overview" && (
          <OverviewComponent
            data={overviewData as Overview}
            startDate={previousLabel}
            endDate={currentLabel}
          />
        )}
        {view === "graph" && GraphComponent && (
          <GraphComponent data={graphData as Graph} />
        )}
        {view === "monthly" && MonthlyComponent && (
          <MonthlyComponent
            data={monthlyData as Monthly}
            clientId={clientId}
            connectionId={connectionId}
            range={range}
          />
        )}
        {view === "raw" && RawComponent && (
          <RawComponent data={rawData as Raw} />
        )}
      </>
    </Background>
  );
}
